import {
  faCheckCircle,
  faTrashAlt,
  faWarning,
  IconDefinition,
} from "@fortawesome/pro-regular-svg-icons";
import { useNavigate } from "react-router-dom";

import { DisplayDateFromNow } from "src/base-components/Date";
import { Pill } from "src/base-components/Pill";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import { TagPill } from "src/base-components/TagPill";
import { useGetCasePropertyIcon } from "src/cases/common/useGetCasePropertyIcon";
import { TemplateStatus, useTemplateStatus } from "src/cases/hooks";
import {
  CasePropertySchema,
  CasePropertyValue as CasePropertyValueType,
  CaseQueueColumnSchema,
} from "src/cases/queues/types";
import { getTemplateIcon } from "src/cases/templates/utils";
import { EntityDetailsWindowPill } from "src/entities/entityView/EntityDetailsWindowPill";
import { Environment } from "src/hooks/useEnvironment";
import {
  DashboardPageParams,
  getUrlToCaseTemplateVersionPage,
} from "src/router/urls";
import { DateType } from "src/utils/datetime";
import { assertUnreachable } from "src/utils/typeUtils";
import { useParamsDecode } from "src/utils/useParamsDecode";

export const CaseTemplateValue: React.FC<{
  templateName: string;
  templateId: string;
  templateIcon: string;
  versionId: string;
  versionName: string;
}> = ({ templateName, templateId, templateIcon, versionId, versionName }) => {
  const { wsId, orgId } = useParamsDecode<DashboardPageParams>();
  const navigate = useNavigate();

  const icon = getTemplateIcon(templateIcon);

  return (
    <Pill
      size="sm"
      variant="gray"
      onClick={(e) => {
        e.stopPropagation();
        navigate(
          getUrlToCaseTemplateVersionPage(orgId, wsId, templateId, versionId),
        );
      }}
    >
      {icon && <Pill.Icon icon={icon} />}
      <Pill.Text>
        {templateName}, {versionName}
      </Pill.Text>
    </Pill>
  );
};

const statusToPillVariant = {
  [TemplateStatus.Outdated]: "orange",
  [TemplateStatus.Deleted]: "red",
  [TemplateStatus.Active]: "green",
} as const;

const statusToStatusIcon = {
  [TemplateStatus.Outdated]: faWarning,
  [TemplateStatus.Deleted]: faTrashAlt,
  [TemplateStatus.Active]: faCheckCircle,
};

export const CaseTemplateStatusValue: React.FC<{
  value: {
    templateId: string;
    schemaVersion: number;
    versionId: string;
    queueId?: string;
  };
}> = ({ value: { templateId, versionId, queueId, schemaVersion } }) => {
  const { status, isLoading } = useTemplateStatus({
    templateId,
    versionId,
    queueId,
    schemaVersion,
  });

  if (isLoading) {
    return <SkeletonPlaceholder height="h-full" width="w-10" />;
  }

  if (!status) return null;

  const icon = statusToStatusIcon[status];
  const variant = statusToPillVariant[status];

  return (
    <Pill size="sm" variant={variant ?? "gray"}>
      <Pill.Icon icon={icon} />
      <Pill.Text>{status}</Pill.Text>
    </Pill>
  );
};

export const CasePropertyValue: React.FC<{
  propertyValue?: CasePropertyValueType;
  propertySchema?: CasePropertySchema;
  columnSchema: CaseQueueColumnSchema;
  environment: Environment;
  entityDisplayName?: string;
}> = ({
  columnSchema,
  propertySchema,
  propertyValue,
  environment,
  entityDisplayName,
}) => {
  const getPropertyIcon = useGetCasePropertyIcon();

  if (!propertySchema) return null;

  const isValidValue = isValidColumnValue(columnSchema, propertySchema);
  const isEmptyValue = typeof propertyValue === "undefined";
  if (!isValidValue || isEmptyValue) return null;

  return renderPropertyValueByType(
    propertySchema,
    propertyValue,
    getPropertyIcon,
    environment,
    entityDisplayName,
  );
};

/**
 * Ensures property is valid in the column
 */
const isValidColumnValue = (
  columnSchema: CaseQueueColumnSchema,
  propertySchema: CasePropertySchema,
) => {
  return (
    columnSchema.display_name === propertySchema.display_name &&
    columnSchema.type === propertySchema.type
  );
};

const renderPropertyValueByType = (
  schema: CasePropertySchema,
  value: CasePropertyValueType,
  getPropertyIcon: (property: CasePropertySchema) => IconDefinition,
  environment: Environment,
  entityDisplayName?: string,
): React.ReactNode => {
  switch (schema?.type) {
    case "string": {
      return <span className="font-inter-normal-12px">{String(value)}</span>;
    }

    case "enum": {
      const enumValue = schema.values.find((v) => v.value === value);
      return <TagPill size="sm" value={enumValue || String(value)} />;
    }

    case "datetime": {
      return (
        <span className="font-inter-normal-12px">
          <DisplayDateFromNow value={value as DateType} allowFuture />
        </span>
      );
    }

    case "tags": {
      if (Array.isArray(value)) {
        return (
          <div className="flex gap-1">
            {value.map((tag, index) => {
              const tagValue = schema.values?.find((v) => v.value === tag);
              return <TagPill key={index} value={tagValue || String(tag)} />;
            })}
          </div>
        );
      }

      return <span className="font-inter-normal-12px">{String(value)}</span>;
    }

    case "entity_ref": {
      const entityId = value as string;
      const displayName = entityDisplayName || entityId;

      return (
        <EntityDetailsWindowPill
          entityIcon={getPropertyIcon(schema)}
          environment={environment}
          id={entityId}
          name={displayName}
          schemaId={schema.entity_type}
        />
      );
    }
    case "event_ref": {
      return <span className="font-inter-normal-12px">{String(value)}</span>;
    }

    case "integer":
    case "number":
      return <span className="font-inter-normal-12px">{String(value)}</span>;

    default:
      assertUnreachable(schema);
  }
};
